import Editor from '@monaco-editor/react'
import {
  useCreateOptimizationTroubleshootLog,
  useDeleteOptimizationTroubleshootLog,
  useOptimizationTroubleshootLogs,
  useUpdateOptimizationTroubleshootLog,
} from 'api/optimizations/optimizations.api'
import {
  OptimizationTroubleshootLog,
  OptimizationTroubleshootLogCreate,
  OptimizationTroubleshootLogUpdate,
} from 'api/optimizations/optimizations.serializers'
import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from 'ui/atoms'
import Alert, { type AlertVariant } from 'ui/components/Alert/Alert'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import Datetime from 'utils/datetime/datetime'
import styles from './OptimizationsDebugView.module.less'

type OptimizationsDebugViewProps = {
  optJobId: number
  queueItemId?: number | null
}
export default function OptimizationsDebugView({ optJobId, queueItemId }: OptimizationsDebugViewProps) {
  const { data: logs, isLoading, isFetching, refetch } = useOptimizationTroubleshootLogs({ optJobId, queueItemId })
  const createMutation = useCreateOptimizationTroubleshootLog()
  const [activeUpdateId, setActiveUpdateId] = useState<number | null>(null)
  const updateMutation = useUpdateOptimizationTroubleshootLog(activeUpdateId ?? 0)
  const deleteMutation = useDeleteOptimizationTroubleshootLog(activeUpdateId ?? 0, { optJobId, queueItemId })
  const { user } = useAuth()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [drafts, setDrafts] = useState<Record<number, { log_message: string; log_level: string; log_data?: string }>>(
    {}
  )
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const [newLog, setNewLog] = useState<{ log_message: string; log_level: string; log_data: string }>({
    log_message: '',
    log_level: 'INFO',
    log_data: '{}',
  })

  // Toast state
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: AlertVariant }>({
    open: false,
    message: '',
    severity: 'error',
  })
  const closeToast = () => setToast((s) => ({ ...s, open: false }))

  const startEdit = useCallback((log: OptimizationTroubleshootLog) => {
    setEditingId(log.id)
    setDrafts((d) => ({
      ...d,
      [log.id]: {
        log_message: log.log_message,
        log_level: log.log_level,
        log_data: log.log_data ? JSON.stringify(log.log_data, null, 2) : '',
      },
    }))
  }, [])

  useEffect(() => {
    if (editingId !== null && editingId !== activeUpdateId) {
      setActiveUpdateId(editingId)
    }
  }, [editingId, activeUpdateId])

  const handleCreate = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!newLog.log_message.trim()) return
      let parsed: unknown = undefined
      if (newLog.log_data.trim()) {
        try {
          parsed = JSON.parse(newLog.log_data)
        } catch {
          setToast({ open: true, message: t('Invalid JSON in log data'), severity: 'error' })
          return
        }
      }
      const payload: OptimizationTroubleshootLogCreate = {
        opt_job: optJobId,
        queue_item: queueItemId ?? 0,
        log_message: newLog.log_message,
        log_level: newLog.log_level,
        log_data: parsed,
      }
      createMutation.mutate(payload, {
        onSuccess: () => setNewLog({ log_message: '', log_level: 'INFO', log_data: '' }),
      })
    },
    [createMutation, newLog, optJobId, queueItemId]
  )

  const handleUpdate = useCallback(() => {
    if (!editingId) return
    const draft = drafts[editingId]
    if (!draft) return
    let parsed: unknown = undefined
    if (draft.log_data?.trim()) {
      try {
        parsed = JSON.parse(draft.log_data)
      } catch {
        setToast({ open: true, message: t('Invalid JSON in log data'), severity: 'error' })
        return
      }
    }
    const payload: OptimizationTroubleshootLogUpdate = {
      log_message: draft.log_message,
      log_level: draft.log_level,
      log_data: parsed,
    }
    setActiveUpdateId(editingId)
    updateMutation.mutate(payload, { onSuccess: () => setEditingId(null) })
  }, [drafts, editingId, updateMutation])

  const handleDelete = useCallback(
    (log: OptimizationTroubleshootLog) => {
      if (confirmDeleteId !== log.id) {
        setConfirmDeleteId(log.id)
        return
      }
      setActiveUpdateId(log.id)
      deleteMutation.mutate(undefined, { onSettled: () => setConfirmDeleteId(null) })
    },
    [confirmDeleteId, deleteMutation]
  )

  const cancelDelete = useCallback(() => setConfirmDeleteId(null), [])

  const sortedLogs = useMemo(() => {
    return [...(logs || [])].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  }, [logs])

  return (
    <div className={styles.OptimizationDebugView}>
      <InfoBanner
        text={t('Here you can share your findings for the selected optimization by posting log messages.')}
        style={'info'}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() => refetch()}
          disabled={isLoading || isFetching}
          icon={isLoading || isFetching ? 'fal fa-spinner fa-spin' : 'fas fa-redo-alt'}
        >
          {isLoading || isFetching ? `${t('Loading')}...` : t('Fetch new log messages')}
        </Button>
      </div>
      {!sortedLogs ||
        (sortedLogs.length === 0 && (
          <span style={{ display: 'flex', justifyContent: 'center', paddingBottom: 8 }}>
            {<i>{t('No log messages yet')}</i>}
          </span>
        ))}

      <section className={styles.OptimizationDebugView_Logs}>
        {sortedLogs?.map((log) => {
          const logMessageHasLogData = Boolean(log.log_data && Object.keys(log.log_data).length > 0)
          const logMessageHasBeenEdited = new Date(log.updated_at).getTime() !== new Date(log.created_at).getTime()
          const isEditingLogMessage = editingId === log.id
          const draft = drafts[log.id]
          const canEditLogMessage = log.created_by === user?.id

          const updatedLogAt = Datetime.toLocalTime(Datetime.toISOString(log.updated_at), 'dateTime')
          return (
            <div key={log.id} className={styles.OptimizationDebugView_LogItem}>
              <div className={styles.OptimizationDebugView_LogItem_MetaInfo}>
                <b>{log.created_by_name}</b>
                {logMessageHasBeenEdited && (
                  <span style={{ fontSize: 12 }}>
                    <i>
                      ({t('Edited')} {updatedLogAt})
                    </i>
                  </span>
                )}
              </div>
              <div className={styles.OptimizationDebugView_LogItem_LogMessage}>
                {isEditingLogMessage ? (
                  <input
                    className={styles.OptimizationDebugView_LogItem_LogMessageInput}
                    value={draft?.log_message ?? ''}
                    onChange={(event) =>
                      setDrafts((draft) => ({
                        ...draft,
                        [log.id]: { ...draft[log.id], log_message: event.target.value },
                      }))
                    }
                    placeholder={t('Log message')}
                  />
                ) : (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{log.log_message}</pre>
                )}
              </div>
              {(isEditingLogMessage || logMessageHasLogData) && (
                <div>
                  {isEditingLogMessage ? (
                    <div className={styles.JsonEditorWrapper}>
                      <Editor
                        height="120px"
                        defaultLanguage="json"
                        theme="vs-light"
                        value={draft?.log_data ?? ''}
                        onChange={(value) =>
                          setDrafts((draft) => ({
                            ...draft,
                            [log.id]: { ...draft[log.id], log_data: value ?? '' },
                          }))
                        }
                        options={{
                          minimap: { enabled: false },
                          wordWrap: 'on',
                          scrollBeyondLastLine: false,
                          fontSize: 12,
                          tabSize: 2,
                          automaticLayout: true,
                        }}
                      />
                    </div>
                  ) : (
                    logMessageHasLogData && (
                      <details>
                        <summary style={{ cursor: 'pointer' }}>{t('Log data')}</summary>
                        <pre style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>
                          {JSON.stringify(log.log_data, null, 2)}
                        </pre>
                      </details>
                    )
                  )}
                </div>
              )}
              <>
                {isEditingLogMessage ? (
                  confirmDeleteId === log.id ? (
                    <div className={styles.OptimizationDebugView_ActionButtonsContainer}>
                      <span className={styles.DangerText}>
                        {t('Are you sure you want to delete this log message?')}
                      </span>
                      <Button danger onClick={() => handleDelete(log)}>
                        {t('Yes')}
                      </Button>
                      <Button onClick={cancelDelete} secondary>
                        {t('No')}
                      </Button>
                    </div>
                  ) : (
                    <div className={styles.OptimizationDebugView_ActionButtonsContainer}>
                      <Button primary onClick={handleUpdate} disabled={updateMutation.status === 'pending'}>
                        {t('Save')}
                      </Button>
                      <Button
                        secondary
                        onClick={() => setEditingId(null)}
                        disabled={updateMutation.status === 'pending'}
                      >
                        {t('Cancel')}
                      </Button>
                      <Button danger onClick={() => handleDelete(log)} icon="fal fa-trash">
                        {t('Delete')}
                      </Button>
                    </div>
                  )
                ) : (
                  <div style={{ marginTop: 32 }}>
                    <div className={styles.OptimizationDebugView_LogItem_LogMessage}>
                      <i>
                        {Datetime.toLocalTime(
                          Datetime.toISOString(log.created_at),
                          'longDayAndMonthTextWithoutYearIfCurrentYear'
                        )}
                      </i>
                      {canEditLogMessage && (
                        <Button onClick={() => startEdit(log)} icon="fal fa-pen">
                          {t('Edit')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </>
            </div>
          )
        })}
      </section>
      <form onSubmit={handleCreate} className={styles.NewLogForm}>
        <div className={styles.FieldColumn}>
          <textarea
            id="new-log-message"
            value={newLog.log_message}
            onChange={(event) => setNewLog((value) => ({ ...value, log_message: event.target.value }))}
            placeholder={t('Write a new log message')}
            required
            rows={3}
            className={styles.TextAreaMessage}
          />
        </div>
        <details className={styles.CollapsibleJson}>
          <summary style={{ cursor: 'pointer' }}>
            <b>{t('Log data in JSON')}</b>
            <span style={{ fontSize: 12 }}>
              <i>{` (${t('Optional')})`}</i>
            </span>
          </summary>
          <div className={styles.JsonEditorContainer}>
            <div id="new-log-data" className={styles.JsonEditorBox}>
              <Editor
                height="180px"
                defaultLanguage="json"
                theme="vs-light"
                value={newLog.log_data}
                onChange={(value) => setNewLog((s) => ({ ...s, log_data: value ?? '' }))}
                options={{
                  minimap: { enabled: false },
                  wordWrap: 'on',
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                  tabSize: 2,
                  automaticLayout: true,
                }}
              />
            </div>
          </div>
        </details>
        <div className={styles.SubmitRow}>
          {createMutation.status === 'error' && <span className={styles.DangerText}>{t('Create failed')}</span>}
          <Button primary disabled={createMutation.status === 'pending'}>
            {createMutation.status === 'pending' ? t('Posting') + '…' : t('Post')}
          </Button>
        </div>
      </form>
      <Alert
        open={toast.open}
        variant={toast.severity}
        message={toast.message}
        onClose={closeToast}
        SnackbarProps={{
          autoHideDuration: 4000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
        }}
      />
    </div>
  )
}
